import type { GeneratedInterpretation, InterpretationClaim } from './interpretation.js';
import type { InterpretationStrength } from './rules.js';

export interface StrengthPresentation {
  level: InterpretationStrength['level'];
  label: '低' | '中' | '高' | '很高';
  explanation: string;
  hasCounterEvidence: boolean;
}

export interface EvidenceSufficiencyPresentation {
  label: '依據較少' | '多條件支持' | '有反向依據';
  independentEvidenceDimensions: number;
  sourceCount: number;
  supportingRuleCount: number;
  counterRuleCount: number;
  explanation: string;
}

export interface ClaimCardViewModel {
  claimKey: string;
  effectDimensions: readonly string[];
  conclusion: string;
  strength: StrengthPresentation;
  supportingRuleCount: number;
  counterRuleCount: number;
  evidenceDiversity: number;
  ruleIds: readonly string[];
  sourceIds: readonly string[];
  sourceLocators: readonly string[];
  ruleProvenance: InterpretationClaim['ruleProvenance'];
  evidenceRefs: readonly string[];
  evidenceGroups: readonly string[];
  culturalFrameworkLabel: '傳統紫微斗數觀點';
  evidenceSufficiency: EvidenceSufficiencyPresentation;
}

export interface TopicInterpretationViewModel {
  topic: GeneratedInterpretation['topic'];
  claims: readonly ClaimCardViewModel[];
  versions: GeneratedInterpretation['versions'];
  disclosure: string;
  decisionBoundary?: string;
}

const LABELS: Readonly<Record<InterpretationStrength['level'], StrengthPresentation['label']>> = {
  low: '低',
  medium: '中',
  high: '高',
  very_high: '很高',
};

export function presentStrength(strength: InterpretationStrength): StrengthPresentation {
  let explanation: string;
  if (strength.level === 'low') {
    explanation = '目前只有少量獨立判讀依據，應視為較弱的傳統命理訊號。';
  } else if (strength.level === 'medium') {
    explanation = '已有多個條件共同支持，但仍應連同反向依據與其他宮位一起閱讀。';
  } else if (strength.level === 'high') {
    explanation = '多個相互獨立的條件一致支持此判讀，仍不代表科學上的準確率。';
  } else {
    explanation = '目前有高度多樣且一致的規則依據；這仍是規則一致性，不是科學預測機率。';
  }
  if (strength.counterRuleCount > 0) {
    explanation += ' 同時存在抵消或反向依據，已保留在詳情中。';
  }
  return {
    level: strength.level,
    label: LABELS[strength.level],
    explanation,
    hasCounterEvidence: strength.counterRuleCount > 0,
  };
}

export function presentEvidenceSufficiency(claim: InterpretationClaim): EvidenceSufficiencyPresentation {
  const supportingRuleCount = Math.max(0, claim.strength.ruleCount - claim.strength.counterRuleCount);
  const counterRuleCount = claim.strength.counterRuleCount;
  const label: EvidenceSufficiencyPresentation['label'] = counterRuleCount > 0
    ? '有反向依據'
    : claim.strength.evidenceDiversity >= 2 && supportingRuleCount >= 2
      ? '多條件支持'
      : '依據較少';
  const explanation = label === '有反向依據'
    ? '支持與抵消條件同時存在，請展開 Evidence 一起閱讀。'
    : label === '多條件支持'
      ? '至少兩個獨立 Evidence 維度共同支持；這仍不是準確率或事件機率。'
      : '目前主要來自單一或少量 Evidence 維度，不應放大解讀。';
  return {
    label,
    independentEvidenceDimensions: claim.strength.evidenceDiversity,
    sourceCount: claim.sourceIds.length,
    supportingRuleCount,
    counterRuleCount,
    explanation,
  };
}

function decisionBoundaryForTopic(topic: GeneratedInterpretation['topic']): string | undefined {
  if (topic === 'wealth') return '財運內容是傳統文化規則式解讀，不應單獨用於投資、借貸、保險、稅務或其他重大財務決策。';
  if (topic === 'career') return '事業內容不代表錄取、升遷、收入或職位結果；重大職涯決策仍應以現實條件與專業資訊為準。';
  if (topic === 'timing') return '運限內容只表示傳統規則在特定時間層的條件變化，不表示事件必然發生。';
  return undefined;
}

export function claimCardViewModel(claim: InterpretationClaim): ClaimCardViewModel {
  return {
    claimKey: claim.claimKey,
    effectDimensions: claim.effectDimensions ?? Object.freeze([]),
    conclusion: claim.text,
    strength: presentStrength(claim.strength),
    supportingRuleCount: Math.max(0, claim.strength.ruleCount - claim.strength.counterRuleCount),
    counterRuleCount: claim.strength.counterRuleCount,
    evidenceDiversity: claim.strength.evidenceDiversity,
    ruleIds: claim.ruleIds,
    sourceIds: claim.sourceIds,
    sourceLocators: claim.sourceLocators,
    ruleProvenance: claim.ruleProvenance,
    evidenceRefs: claim.evidenceRefs,
    evidenceGroups: claim.evidenceGroups,
    culturalFrameworkLabel: '傳統紫微斗數觀點',
    evidenceSufficiency: presentEvidenceSufficiency(claim),
  };
}

export function topicInterpretationViewModel(result: GeneratedInterpretation): TopicInterpretationViewModel {
  const decisionBoundary = decisionBoundaryForTopic(result.topic);
  return {
    topic: result.topic,
    claims: result.claims.map(claimCardViewModel),
    versions: result.versions,
    disclosure: '以下內容為傳統紫微斗數規則式解讀與自我探索資訊，不是科學已證實的預測或專業決策建議。',
    ...(decisionBoundary !== undefined ? { decisionBoundary } : {}),
  };
}
